/**
 * Mid-level helper functions for video template operations
 * Functions that interact with stores and services but can be extracted from the main component
 */

import { TimelineMode } from '@src/editor/commands';
import {
  deleteElementMeta,
  storeCompositionTemplateMetadata,
} from '@src/utility/extraElementData';

import type { CompositionTemplate } from '../CompositionSettings/types';
import type { VideoTemplateElementType } from './VideoTemplatesContext';
import type { Element } from '@src/editor/models/Element';
import type {
  AddMediaToTimelineOptions,
  LogoToTimelineOptions,
  MediaFileItem,
  MediaFilePlaceholder,
  MediaType,
} from '@src/hooks/useAddMediaToTimeline';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { TranscriptElementWithClipSegmentAttributes } from '@src/types.ts/transcript';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

/**
 * Removes multiple elements from the editor and cleans up their metadata.
 * Uses commandRemoveElement which includes scene adjustment logic.
 *
 * Special handling for intro cards: When an intro card is removed, all elements
 * on all tracks are shifted left by the intro card's duration to reverse the
 * ripple effect that was applied when the intro card was added.
 */
export function removeElementsAndMetadata(
  elements: { id: string; type: VideoTemplateElementType }[],
  editorStore: EditorStoreLike,
  videoCreator: IVideoCreatorStore,
  commandDeleteKaraoke: (createUndoRedoEntry: boolean) => void,
  createUndo: boolean,
): void {
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const isCreateUndo = createUndo && i === 0;

    if (element.type === 'captions') {
      commandDeleteKaraoke(isCreateUndo);
    } else if (element.type === 'introCard') {
      // Special handling for intro card: need to shift all tracks left after removal
      removeIntroCardWithRipple(
        element.id.toString(),
        editorStore,
        videoCreator,
        isCreateUndo,
      );
    } else {
      editorStore.editor.commandRemoveElement(
        element.id.toString(),
        isCreateUndo,
      );
      if (createUndo) {
        deleteElementMeta(videoCreator.currentVideo, element.id.toString());
      }
    }
  }
}

/**
 * Removes an intro card and shifts all elements on all tracks left by its duration.
 * This reverses the RIPPLE_ALL_TRACKS effect that was applied when the intro card was added.
 * Uses TimelineMode.RIPPLE_ALL_TRACKS for consistent behavior with add operations.
 */
function removeIntroCardWithRipple(
  elementId: string,
  editorStore: EditorStoreLike,
  videoCreator: IVideoCreatorStore,
  createUndo: boolean,
): void {
  const editor = editorStore.editor;

  // Remove the intro card element with RIPPLE_ALL_TRACKS mode
  // This will automatically shift all elements on all tracks left by the intro card's duration
  editor.commandRemoveElement(
    elementId,
    createUndo,
    TimelineMode.RIPPLE_ALL_TRACKS,
  );

  // Clean up metadata
  if (createUndo) {
    deleteElementMeta(videoCreator.currentVideo, elementId);
  }
}

/**
 * Removes template elements directly from the timeline WITHOUT triggering scene adjustment.
 * This is used when clearing/swapping templates to preserve user-created scenes.
 * Uses the low-level timeline.removeElement() method instead of commandRemoveElement.
 */
export function removeTemplateElementsWithoutSceneAdjustment(
  elements: { id: string; type: VideoTemplateElementType }[],
  editorStore: EditorStoreLike,
  videoCreator: IVideoCreatorStore,
  commandDeleteKaraoke: (createUndoRedoEntry: boolean) => void,
): void {
  for (const element of elements) {
    if (element.type === 'captions') {
      commandDeleteKaraoke(false);
    } else if (element.id) {
      // Use direct timeline removal to bypass scene adjustment in RemoveElementCommand
      editorStore.editor.timeline.removeElement(element.id.toString());
    }
  }
  // Mark timeline dirty and trigger sync
  editorStore.editor.markDirty('template_elements_removed');
}

/**
 * Builds video props object needed for template operations.
 *
 * When editorStore is provided, transcript elements are read from the editor
 * instead of rootStore.transcriptionStore. This is required in Clip Preview
 * context where rootStore.transcriptionStore contains the parent video's
 * transcript, not the clip's.
 */
export function buildVideoProps(
  videoCreator: IVideoCreatorStore,
  editorStore?: EditorStoreLike,
) {
  // Prefer editor transcript when available (correct for both Creator Studio
  // and Clip Preview). Falls back to transcriptionStore for backwards compat.
  const transcriptElements = editorStore
    ? (editorStore.editor.getFullTranscript() as TranscriptElementWithClipSegmentAttributes[])
    : videoCreator.rootStore?.transcriptionStore?.getFinalTranscriptionElements();

  return {
    organization: videoCreator.organization,
    story: videoCreator.story,
    convertToVh: (v: string) => videoCreator.textProducer.convertToVh(v),
    aspectRatio: videoCreator.getCurrentAspectRatio(),
    getCurrentVideo: () => videoCreator.currentVideo,
    transcriptElements,
    storyId: videoCreator.storyId,
    duration: videoCreator.duration, // necessary for clip adapter support
    time: videoCreator.time,
  };
}

/**
 * Creates standard callbacks for template operations
 */
export function createTemplateCallbacks(
  videoCreator: IVideoCreatorStore,
  addMediaToTimeline: (
    item: MediaFileItem | MediaFilePlaceholder,
    mediaType: MediaType,
    options: AddMediaToTimelineOptions | LogoToTimelineOptions,
    shouldSetActiveElement: boolean,
    createUndoEntry: boolean,
  ) => Promise<{ success: boolean; element: Element | null }>,
) {
  return {
    upsertExtraElementDataCb: (
      elementId: string,
      template: CompositionTemplate,
    ) => {
      storeCompositionTemplateMetadata(
        videoCreator.currentVideo,
        elementId,
        template,
      );
    },
    addMediaToTimelineCb: (
      item: MediaFileItem | MediaFilePlaceholder,
      mediaType: MediaType,
      options: AddMediaToTimelineOptions | LogoToTimelineOptions = {},
    ) => addMediaToTimeline(item, mediaType, options, false, false),
  };
}

/**
 * Cleans up metadata for template elements
 */
export function cleanupTemplateMetadata(
  videoCreator: IVideoCreatorStore,
  elements: { id: string; type: VideoTemplateElementType }[],
): void {
  for (const element of elements) {
    deleteElementMeta(videoCreator.currentVideo, element.id.toString());
  }
}

/**
 * Converts a template ID (which may be various types) to a string
 */
export function normalizeTemplateId(
  id: string | number | boolean | null,
): string {
  return typeof id === 'string' ? id : String(id);
}
